import React, { Component } from "react";
import { MDBBtn } from "mdb-react-ui-kit";

/**
 * URL del tema + reproductor (play/pausa/parar + slider) AUTÓNOMO.
 * El <audio> vive aquí dentro y toda la lógica también.
 *
 * Props:
 *  - url: string
 *  - onUrlChange(url: string): void
 */
export default class ThemeAudioSection extends Component {
  static defaultProps = {
    url: "",
  };

  constructor(props) {
    super(props);
    this.state = {
      canPlay: false,
      playing: false,
      duration: 0,      // segundos
      currentTime: 0,   // segundos
    };
    this.audioRef = React.createRef();
    this._lastUrl = null;
  }

  componentDidMount() {
    this.attachAudioListeners();
    this.syncUrlToAudio(true);
  }

  componentDidUpdate(prevProps) {
    if (this.props.url !== prevProps.url) {
      this.syncUrlToAudio(true);
    }
  }

  componentWillUnmount() {
    this.detachAudioListeners();
    const a = this.audioRef.current;
    if (a) { try { a.pause(); } catch {} }
  }

  // ---------- Audio wiring ----------
  attachAudioListeners = () => {
    const a = this.audioRef.current;
    if (!a) return;
    a.addEventListener("loadedmetadata", this.onLoaded);
    a.addEventListener("canplay", this.onCanPlay);
    a.addEventListener("timeupdate", this.onTimeUpdate);
    a.addEventListener("ended", this.onEnded);
  };

  detachAudioListeners = () => {
    const a = this.audioRef.current;
    if (!a) return;
    a.removeEventListener("loadedmetadata", this.onLoaded);
    a.removeEventListener("canplay", this.onCanPlay);
    a.removeEventListener("timeupdate", this.onTimeUpdate);
    a.removeEventListener("ended", this.onEnded);
  };

  syncUrlToAudio = (resetState = false) => {
    const a = this.audioRef.current;
    const { url } = this.props;
    if (!a) return;

    if (!url) {
      // no hay URL: resetea y deshabilita
      try { a.pause(); } catch {}
      a.removeAttribute("src");
      a.load();
      this.setState({ canPlay: false, playing: false, duration: 0, currentTime: 0 });
      this._lastUrl = null;
      return;
    }

    if (url !== this._lastUrl) {
      // Asignar nueva URL y resetear
      try { a.pause(); } catch {}
      a.src = url;
      a.load();
      this._lastUrl = url;
      if (resetState) {
        this.setState({ canPlay: false, playing: false, duration: 0, currentTime: 0 });
      }
    }
  };

  // ---------- Audio events ----------
  onLoaded = () => {
    const a = this.audioRef.current;
    if (!a) return;
    this.setState({ duration: a.duration || 0 });
  };

  onCanPlay = () => {
    const a = this.audioRef.current;
    if (!a) return;
    this.setState({ canPlay: true, duration: a.duration || 0 });
  };

  onTimeUpdate = () => {
    const a = this.audioRef.current;
    if (!a) return;
    this.setState({ currentTime: a.currentTime || 0 });
  };

  onEnded = () => {
    this.setState({ playing: false, currentTime: 0 });
  };

  // ---------- Controls ----------
  formatTime = (s) => {
    if (!isFinite(s) || s < 0) s = 0;
    const m = Math.floor(s / 60);
    const ss = Math.floor(s % 60);
    return `${m}:${ss.toString().padStart(2, "0")}`;
  };

  handleUrlInput = (e) => {
    this.props.onUrlChange && this.props.onUrlChange(e.target.value);
  };

  handlePlay = () => {
    const a = this.audioRef.current;
    if (!a) return;
    a.play().then(() => {
      this.setState({ playing: true });
    }).catch(() => {});
  };

  handlePause = () => {
    const a = this.audioRef.current;
    if (!a) return;
    a.pause();
    this.setState({ playing: false });
  };

  handleStop = () => {
    const a = this.audioRef.current;
    if (!a) return;
    a.pause();
    a.currentTime = 0;
    this.setState({ playing: false, currentTime: 0 });
  };

  handleSeek = (e) => {
    const a = this.audioRef.current;
    if (!a) return;
    const v = Number(e.target.value);
    a.currentTime = isFinite(v) ? v : 0;
    this.setState({ currentTime: a.currentTime });
  };

  render() {
    const { url } = this.props;
    const { canPlay, playing, duration, currentTime } = this.state;

    const disabled = !url;
    const sliderDisabled = !canPlay || !isFinite(duration) || duration <= 0;
    const safeCurrent = Math.min(Math.max(currentTime || 0, 0), duration || 0);

    return (
      <div className="mb-4 theme-audio-section">
        {/* Elemento de audio interno, oculto */}
        <audio ref={this.audioRef} preload="metadata" style={{ display: "none" }} />

        <div className="mb-1" style={{ fontSize: ".9rem", opacity: 0.8 }}>
          URL del tema (audio)
        </div>

        <input
          type="url"
          value={url}
          onChange={this.handleUrlInput}
          placeholder="https://… (mp3, ogg, etc.)"
          style={{
            width: "100%",
            fontSize: "1rem",
            padding: "0.55rem 0.7rem",
            borderRadius: 8,
            border: "1px solid rgba(0,0,0,.15)",
            background: "var(--color-tarjeta,#fff)",
            marginBottom: ".5rem",
          }}
        />

        <div className="d-flex align-items-center" style={{ gap: ".5rem", flexWrap: "wrap" }}>
          <MDBBtn size="sm" color="primary" disabled={disabled || playing} onClick={this.handlePlay}>
            ▶️ Reproducir
          </MDBBtn>
          <MDBBtn size="sm" color="secondary" disabled={disabled || !playing} onClick={this.handlePause}>
            ⏸️ Pausar
          </MDBBtn>
          <MDBBtn size="sm" color="dark" disabled={disabled} onClick={this.handleStop}>
            ⏹️ Parar
          </MDBBtn>

          <div style={{ flex: 1, minWidth: 220 }} className="d-flex align-items-center">
            <span
              style={{
                width: 48,
                textAlign: "right",
                fontVariantNumeric: "tabular-nums",
                opacity: 0.8,
              }}
            >
              {this.formatTime(safeCurrent)}
            </span>
            <input
              type="range"
              min={0}
              max={duration || 0}
              step={0.01}
              value={safeCurrent}
              onChange={this.handleSeek}
              disabled={sliderDisabled}
              style={{ flex: 1, margin: "0 .5rem" }}
            />
            <span
              style={{
                width: 48,
                fontVariantNumeric: "tabular-nums",
                opacity: 0.8,
              }}
            >
              {this.formatTime(duration || 0)}
            </span>
          </div>
        </div>
      </div>
    );
  }
}
